import { extractFeatureFlagsCookies } from '@seek/candidate-feature-flag';
import {
  getSiteNameFromHostname,
  type Language,
  type Locale,
  type SiteName,
  siteConfig,
  createAbsoluteUrl,
  defaultLanguage,
  getLocale,
  languages,
  type SiteConfig,
} from '@seek/melways-sites';
import type express from 'express';
import { createContext, useContext } from 'react';

import {
  developmentOverrideSource,
  stagingOverrideSource,
} from 'src/core/domain/launchDarklyOverrideSource';
import { getTimezoneFromZone } from 'src/core/utils/textUtil';
import { countryToZoneMap } from 'src/mapper/countryToZoneMapper';
import type { ConfigStateContext } from 'src/types';
import type {
  CareerHubCountry,
  CareerHubBrand,
  CareerHubLocale,
  CareerHubSite,
  CareerHubLanguage,
} from 'src/types/ConfigStateContext';
import type { Environment } from 'src/types/clientContext';

const DEFAULT_LOCALE = 'en-AU';
const DEFAULT_LANGUAGE = defaultLanguage; // en

interface ConfigState extends ConfigStateContext {
  clientOrServer: 'client' | 'server';
}

const ConfigContext = createContext<ConfigStateContext | undefined>(undefined);

type ConfigProviderProps = React.PropsWithChildren<{
  config: ConfigState;
}>;

export const ConfigProvider = ({ children, config }: ConfigProviderProps) => (
  <ConfigContext.Provider value={config}>{children}</ConfigContext.Provider>
);

export const useConfig = () => {
  const context = useContext(ConfigContext);

  if (context === undefined) {
    throw new Error('useConfig must be used within a ConfigProvider');
  }

  return context;
};

const getSiteNameWithFallback = (hostname: string): CareerHubSite => {
  try {
    return getSiteNameFromHostname(hostname) as CareerHubSite;
  } catch {
    return 'candidate-jobstreet-my';
  }
};

export const createConfig = (req: express.Request): ConfigStateContext => {
  const req_hostname = req.hostname;
  const pathname = req.originalUrl;
  const path = req.path;
  // Melways should give us the x-forwarded-host as a string. If it's not, this request likely isn't using Melways.
  const xForwardedHost =
    typeof req.headers['x-forwarded-host'] === 'string'
      ? req.headers['x-forwarded-host']
      : undefined;

  // Use the production value hostname for all values
  const hostname = getProdHostName(xForwardedHost || req_hostname).replace(
    'dev.',
    '',
  );

  const { ENVIRONMENT } = process.env;
  const environment = (ENVIRONMENT || 'development') as Environment;

  const siteName = getSiteNameWithFallback(hostname);
  const site = siteConfig[siteName];

  const language = getLanguage(pathname);
  const locale = getMelwaysLocale(siteName, language);
  const country = getCountryCode(site);
  const zone = countryToZoneMap[country];

  const absoluteUrl = createAbsoluteUrl({
    product: site.classification.product,
    country,
    path: req.originalUrl.replace(`/${language}/`, '/'),
    language,
  });

  const solId = getSolId(req);
  const launchDarklyOverrides = getLaunchDarklyOverrides(req, environment);

  const config: ConfigState = {
    language,
    locale,
    site: siteName,
    path,
    countryCode: country,
    zone: countryToZoneMap[country],
    brand: BRAND_INFO[country],
    absoluteUrl,
    environment,
    clientOrServer: 'server',
    timezone: getTimezoneFromZone(zone),
    solId,
    launchDarklyOverrides,
  };
  return config;
};

export const getLanguage = (pathname: string): CareerHubLanguage => {
  // Check if the path starts with a 2-letter language code eg., /th/
  const languageFromPath = pathname.split('/')[1];

  return parseLanguage(languageFromPath) as CareerHubLanguage;
};

const parseLanguage = (maybeLanguage: string): Language =>
  findLanguage(maybeLanguage) || DEFAULT_LANGUAGE;

export const getMelwaysLocale = (
  site: SiteName,
  language: Language,
): CareerHubLocale => {
  const melLocale = getLocale(site, language);
  return parseLocale(melLocale);
};

const parseLocale = (maybeLocale: Language | Locale): CareerHubLocale => {
  if (!isLanguage(maybeLocale)) {
    return maybeLocale as CareerHubLocale;
  }

  return DEFAULT_LOCALE as CareerHubLocale;
};

const isLanguage = (maybeLanguage: string): maybeLanguage is Language =>
  findLanguage(maybeLanguage) !== undefined;

const findLanguage = (maybeLanguage: string): Language | undefined =>
  [...languages, 'ph' as Language].find(
    (validLang) => validLang === maybeLanguage,
  );

const getSolId = (req: express.Request): string => req.cookies.sol_id || '';

const getLaunchDarklyOverrides = (
  req: express.Request,
  environment: Environment,
): Record<string, string> => {
  const overridesFromCookies = extractFeatureFlagsCookies(req.cookies || {});

  if (environment === 'development') {
    return { ...developmentOverrideSource, ...overridesFromCookies };
  }

  if (environment === 'staging') {
    return { ...stagingOverrideSource, ...overridesFromCookies };
  }

  // Production only accept overrides from cookies (client side)
  return overridesFromCookies;
};

export const getProdHostName = (hostname: string): string => {
  const regex = /\.(local|staging)/i;

  return hostname.replace(regex, '');
};

export const getCountryCode = (site: SiteConfig): CareerHubCountry => {
  if (typeof site.classification === 'boolean') {
    throw Error(`Site "${site.host}" does not have a country.`);
  }

  return (site.classification.country as CareerHubCountry) || 'id';
};

const BRAND_INFO: { [key in CareerHubCountry]: CareerHubBrand } = {
  my: 'jobstreet',
  sg: 'jobstreet',
  id: 'jobstreet',
  ph: 'jobstreet',
  au: 'seek',
  nz: 'seek',
  hk: 'jobsdb',
  th: 'jobsdb',
};

export default ConfigProvider;
